// src/components/panel/WeekMonthSummary.js
import React, { useState, useEffect, useCallback } from 'react';
import { CalendarOutlined, BarChartOutlined } from '@ant-design/icons';
import { Spin, Select } from 'antd';
import analyticsV2Service, { getDateRanges } from '../../services/analyticsV2Service';
import './WeekMonthSummary.css';

function WeekMonthSummary() {
    const [weekData, setWeekData] = useState(null);
    const [monthData, setMonthData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [monthPeriod, setMonthPeriod] = useState('currentMonth'); // 'currentMonth' or 'prevMonth'

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            
            // Get date ranges for week and month
            const ranges = getDateRanges();
            
            // Fetch week data and month data in parallel
            const [weekResponse, monthResponse] = await Promise.all([
                analyticsV2Service.getSalesSummary(ranges.week.start, ranges.week.end),
                monthPeriod === 'currentMonth' 
                    ? analyticsV2Service.getSalesSummary(ranges.currentMonth.start, ranges.currentMonth.end)
                    : analyticsV2Service.getSalesSummary(ranges.prevMonth.start, ranges.prevMonth.end)
            ]);
            
            if (weekResponse.success && weekResponse.data) {
                const summary = weekResponse.data.summary;
                setWeekData({
                    InvoiceCount: summary.invoiceCount || 0,
                    TotalSales: summary.totalSales || 0,
                    AvgInvoiceValue: summary.avgTicket || 0
                });
            }
            
            if (monthResponse.success && monthResponse.data) {
                const summary = monthResponse.data.summary;
                setMonthData({
                    InvoiceCount: summary.invoiceCount || 0,
                    TotalSales: summary.totalSales || 0,
                    AvgInvoiceValue: summary.avgTicket || 0
                });
            }
            
            setError(null);
        } catch (err) {
            console.error('Error loading summary:', err);
            setError('Error al cargar resumen');
        } finally {
            setLoading(false);
        }
    }, [monthPeriod]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    if (loading) {
        return (
            <div className='card'>
                <div className='card-content-center'>
                    <Spin tip="Cargando resumen..." />
                </div>
            </div>
        );
    }

    if (error || (!weekData && !monthData)) {
        return (
            <div className='card'>
                <div className='card-content-center' style={{ color: '#ff4d4f' }}>
                    {error || 'No se pudo cargar el resumen'}
                </div>
            </div>
        );
    }

    const monthPeriodOptions = [
        { value: 'currentMonth', label: 'Este Mes' },
        { value: 'prevMonth', label: 'Mes Pasado' }
    ];

    return (
        <div className='card week-month-summary'>
            <div className='card-inner'>
                <BarChartOutlined className='card_icon' style={{ fontSize: '38px' }} />
                <h2>RESUMEN PERÍODO</h2>
            </div>
            <div className='week-month-summary-grid'>
                <div className='week-month-section'>
                    <div className='week-month-header'>
                        <CalendarOutlined /> 
                        <strong>Esta Semana</strong>
                    </div>
                    <div className='week-month-row'>
                        <span>Facturas:</span>
                        <span className='week-month-value'>{weekData?.InvoiceCount || 0}</span>
                    </div>
                    <div className='week-month-row'>
                        <span>Ventas:</span>
                        <span className='week-month-value'>${weekData?.TotalSales?.toLocaleString('es-AR', { minimumFractionDigits: 2 }) || '0.00'}</span>
                    </div>
                    <div className='week-month-row'>
                        <span>Promedio:</span>
                        <span className='week-month-value'>${weekData?.AvgInvoiceValue?.toLocaleString('es-AR', { minimumFractionDigits: 2 }) || '0.00'}</span>
                    </div>
                </div>

                <div className='week-month-divider'></div>

                <div className='week-month-section'>
                    <div className='week-month-header'>
                        <CalendarOutlined /> 
                        <Select
                            value={monthPeriod}
                            onChange={setMonthPeriod}
                            options={monthPeriodOptions}
                            style={{ minWidth: '140px' }}
                            variant="borderless"
                            size="large"
                            className="week-month-selector"
                        />
                    </div>
                    <div className='week-month-row'>
                        <span>Facturas:</span>
                        <span className='week-month-value'>{monthData?.InvoiceCount || 0}</span>
                    </div>
                    <div className='week-month-row'>
                        <span>Ventas:</span>
                        <span className='week-month-value'>${monthData?.TotalSales?.toLocaleString('es-AR', { minimumFractionDigits: 2 }) || '0.00'}</span>
                    </div>
                    <div className='week-month-row'>
                        <span>Promedio:</span>
                        <span className='week-month-value'>${monthData?.AvgInvoiceValue?.toLocaleString('es-AR', { minimumFractionDigits: 2 }) || '0.00'}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default WeekMonthSummary;
